import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { Form } from "react-form";
import { connect } from "react-redux";

import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core";

import AppContainer from "../common/AppContainer";
import PageHeader from "../common/PageHeader";
import RichTextField from "../common/RichTextField";

import { Proposal } from "./models";

const getProposal = Proposal.selectByUrlId(proposal => ({
    formData: proposal.ref,
    ...proposal.ref
}));

// Once feedback has been entered and saved, the field locks - there's no separate submitted/status flag to track.
function hasFollowUpNotes(html) {
    if (!html) return false;
    return html.replace(/<[^>]+>/g, "").trim().length > 0;
}

// Applicants can respond only while the proposal is in Reviewed status; admins/staff retain access afterward.
const POST_REVIEW_STATUSES = ["Hearing", "No Hearing", "Funding Selected", "Funding Rejected"];

function canAccessFollowup(proposal, authState) {
    if (proposal.status === "Reviewed") return true;
    const is_admin = !!(authState && authState.user && (authState.user.role === "admin" || authState.user.role === "readonly"));
    return is_admin && POST_REVIEW_STATUSES.includes(proposal.status);
}

const styles = theme => ({
    rightAlign: {
        textAlign: "right"
    },
    button: {
        marginRight: theme.spacing(2)
    }
});

class Followup extends Component {
    componentDidMount() {
        const { match, authState, ormProposalLoadDetail } = this.props;
        document.title = "Proposal: Review Follow-up - Lessard Sams Outdoor Heritage Council";
        if (authState && authState.user) {
            ormProposalLoadDetail(match.params.id);
        }
    }

    componentDidUpdate() {
        const { proposal, match, authState, ormProposalLoadDetail } = this.props;
        if ((!proposal || !proposal.id) && authState && authState.user) {
            ormProposalLoadDetail(match.params.id);
        }
    }

    updateProposal = values => {
        const { proposal, ormProposalUpdate, history } = this.props;
        ormProposalUpdate({
            id: proposal.id,
            follow_up_notes: values.follow_up_notes
        });
        history.push("/dashboard/");
    };

    render() {
        const { classes, proposal, authState, history } = this.props;

        if (!proposal || !proposal.id) {
            return <AppContainer authenticated synced={true} />;
        }

        if (!canAccessFollowup(proposal, authState)) {
            return (
                <AppContainer authenticated>
                    <Grid container spacing={2}>
                        <PageHeader title="Proposal Review Follow-up" />
                        <Grid item xs={12}>
                            <Typography gutterBottom>
                                Follow-up review is not currently available for this proposal.
                            </Typography>
                            <Button variant="contained" onClick={() => history.push("/dashboard/")}>
                                Return to Dashboard
                            </Button>
                        </Grid>
                    </Grid>
                </AppContainer>
            );
        }

        const already_submitted = hasFollowUpNotes(proposal.follow_up_notes);
        const is_staff = !!(authState && authState.user && (authState.user.role === "admin" || authState.user.role === "readonly"));
        const is_read_only = already_submitted || is_staff;

        return (
            <AppContainer authenticated>
                <Grid container spacing={2}>
                    <PageHeader title={"Proposal Review Follow-up (Optional): ML " + proposal.ml_year + " " + proposal.name} />
                    <Grid item xs={12}>
                        <Form
                            dontValidateOnMount={true}
                            validateOnSubmit={true}
                            defaultValues={proposal.formData}
                            onSubmit={this.updateProposal}>
                            {formApi => (
                                <form onSubmit={formApi.submitForm}>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} lg={7}>
                                            <Typography>
                                                You may choose to respond in writing to questions or comments about your proposal received from Council members. Questions are written in Council member’s proposal evaluation forms and summarized in documents published at the July Council meeting. You may also include additional questions or comments received from members during the proposal review and hearing selection portion of the July Council meeting.  <br/><br/>
                                                You may choose to include direct quotes from Council member comments or questions. If so, please bold this text and note which member asked the question. You may also choose to respond generally to overall feedback or provide clarifications to commonly asked questions or comments.  <br/><br/>
                                                Responses should be directly related to feedback received and should not be used to advocate for your proposal in general.   <br/><br/>
                                                Responses are limited to text entries and must be less than 750 words. <br/><br/>
                                                <b>Press “Save and Submit Feedback to LSOHC” when complete. Responses may not be edited after submission.</b><br/><br/>
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            {already_submitted && (
                                                <Typography gutterBottom>
                                                    <b>Your feedback has been submitted and can no longer be edited.</b>
                                                </Typography>
                                            )}
                                            {!already_submitted && is_staff && (
                                                <Typography gutterBottom>
                                                    <b>This field can only be edited by the project manager.</b>
                                                </Typography>
                                            )}
                                            <RichTextField
                                                field="follow_up_notes"
                                                label="Feedback Notes"
                                                disabled={is_read_only}
                                                maxWords={750}
                                            />
                                        </Grid>
                                        <Grid item xs={12} className={classes.rightAlign}>
                                            <Button
                                                variant="contained"
                                                className={classes.button}
                                                onClick={() => history.push("/dashboard/")}>
                                                Return to Dashboard
                                            </Button>
                                            <Button
                                                variant="contained"
                                                type="submit"
                                                color="primary"
                                                disabled={is_read_only}>
                                                Save and Submit Feedback to LSOHC
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            )}
                        </Form>
                    </Grid>
                </Grid>
            </AppContainer>
        );
    }
}

Followup = connect(
    (state, ownProps) => ({
        proposal: getProposal(state, ownProps),
        authState: state.auth
    }),
    {
        ...Proposal.actions
    }
)(Followup);

export default withStyles(styles)(withRouter(Followup));
